import React from 'react';
import connect from 'react-redux/es/connect/connect';
import { history, store } from '../../helpers/index';
import PropTypes from 'prop-types';
import {
  Modal,
  Form,
  TextArea,
  Button,
  Image,
  Checkbox,
  List,
} from 'semantic-ui-react';
import TagsInput from 'react-tagsinput';
import AutosizeInput from 'react-input-autosize';
import Strings from '../../common/Strings';
import './InviteDialog.css';
import { inviteActions } from '../../actions';
import config from '../../config';

const EMAIL_REGEX = /^([a-zA-Z0-9_+\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5}){1,25}(,[ ]{0,1}([a-zA-Z0-9_+\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5}){1,25})*$/;

class InviteDialog extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      emails: [],
      note: '',
      showTeamModal: false,
      teamModalEmails: [],
    };
  }

  onEmailsChange = emails => {
    const added = emails.length > this.state.emails.length;
    this.setState({ emails });

    // If not a premium user, check if max user count is passed
    if (this.userLimitPassedAfter(emails) && added)
      history.push('#invitePremium', history.location.state);
  };

  // Returns whether adding the members in the input would pass the user limit
  userLimitPassedAfter(emails = this.state.emails) {
    if (this.props.subscription.status === 'active') return false;
    const teamEmails = new Set();

    this.props.projects.forEach(p =>
      p.teamMembers.forEach(m => teamEmails.add(m.email)),
    );

    emails.forEach(e => teamEmails.add(e));

    return teamEmails.size > config.MAX_MEMBERS_FREE;
  }

  onNoteChange = (e, { name, value }) => {
    this.setState({ [name]: value });
  };

  onClickInviteDirectory = () => {
    if (!this.props.isInviting) {
      this.setState({ showTeamModal: true });
    }
  };

  onClickSendInvites = () => {
    if (this.userLimitPassedAfter()) {
      history.push('#invitePremium', history.location.state);
      return;
    }

    const { companyName, defaultCurrency } = store.getState().user;
    const { project } = this.props;
    const { emails, note } = this.state;
    this.props.dispatch(
      inviteActions.inviteTeam(
        companyName,
        project,
        emails,
        note,
        defaultCurrency,
      ),
    );
  };

  renderInviteModal() {
    const { project, isInviting, teamMembers } = this.props;
    const { emails, note, showTeamModal } = this.state;
    return (
      <Modal
        centered
        open={!showTeamModal}
        size="tiny"
        closeIcon={!isInviting}
        onClose={() => {
          if (!isInviting) history.goBack();
        }}
      >
        <div className="invite-modal-root">
          <div className="invite-modal-top-container">
            <p className="invite-modal-title">{Strings.INVITE.share}</p>
            <p className="invite-modal-project-name">{project.name}</p>
          </div>
          <div className="invite-modal-content">
            <div className="invite-modal-input-container">
              <TagsInput
                disabled={isInviting}
                value={emails}
                onChange={this.onEmailsChange}
                inputProps={{ placeholder: Strings.INVITE.emailHint }}
                validationRegex={EMAIL_REGEX}
                addKeys={[9, 13, 32, 186, 188]} // tab, enter, space, semicolon, comma
                onlyUnique
                addOnPaste
                addOnBlur
                pasteSplit={data => {
                  return data
                    .replace(/[\r\n,;]/g, ' ')
                    .split(' ')
                    .map(d => d.trim());
                }}
                renderInput={({ addTag, ...props }) => {
                  const { onChange, value, ...other } = props;
                  return (
                    <AutosizeInput
                      id="email-input"
                      type="text"
                      onChange={onChange}
                      value={value}
                      inputStyle={{
                        backgroundColor: 'transparent',
                        border: '1px solid #D8D8D8',
                        borderTop: 0,
                        borderLeft: 0,
                        borderRight: 0,
                        color: '#4A4A4A',
                        outline: 'none',
                        fontFamily: 'Arial',
                        fontSize: '1em',
                        paddingTop: '0.5rem',
                        paddingBottom: '0.5rem',
                        marginBottom: '0.5rem',
                        minWidth: 90,
                      }}
                      {...other}
                    />
                  );
                }}
              />
              {teamMembers.length > 0 && (
                <Image
                  id="invite-directory-image"
                  src="/icon/invite_directory.svg"
                  disabled={isInviting}
                  onClick={this.onClickInviteDirectory}
                />
              )}
            </div>
            <Form>
              <TextArea
                id="invite-modal-notes-input"
                name="note"
                placeholder={Strings.INVITE.notesHint}
                onChange={this.onNoteChange}
                disabled={isInviting}
                value={note}
                fluid
              />
            </Form>
            <Button
              disabled={isInviting || emails.length === 0}
              id="send-invites-button"
              className="common-green-button"
              onClick={this.onClickSendInvites}
              loading={isInviting}
            >
              {Strings.INVITE.sendInvites}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  onTeamCheckboxChange = (event, { id, checked }) => {
    let { teamModalEmails } = this.state;

    if (checked) {
      teamModalEmails.push(id);
    } else {
      teamModalEmails = teamModalEmails.filter(email => email !== id);
    }

    this.setState({ teamModalEmails });
  };

  onSelectAllCheckboxChange = (event, { checked }) => {
    const { teamMembers } = this.props;
    const teamModalEmails = [];
    if (checked) {
      teamMembers.forEach(t => teamModalEmails.push(t.email));
      this.setState({ teamModalEmails });
    } else {
      this.setState({ teamModalEmails });
    }
  };

  onClickAdd = () => {
    const { teamModalEmails, emails } = this.state;
    // Filter already existing ones
    let emailsToAdd = teamModalEmails.filter(
      email => !emails.find(e => e === email),
    );
    emailsToAdd.forEach(email => emails.push(email));
    this.setState({ showTeamModal: false, teamModalEmails: [] });
  };

  renderTeamModal() {
    let { teamMembers } = this.props;
    const { teamModalEmails } = this.state;
    const TeamMembers = teamMembers.map(teamMember => {
      return (
        <List.Item className="invite-member-list-item" key={teamMember.email}>
          <div className="invite-modal-members-list-item-content">
            <Checkbox
              id={teamMember.email}
              checked={teamModalEmails.find(
                email => email === teamMember.email,
              )}
              onChange={this.onTeamCheckboxChange}
            />
            <div style={{ flexGrow: 1, marginLeft: '0.75rem' }}>
              <p className="invite-modal-members-list-item-name">
                {teamMember.name || teamMember.email}
              </p>
              {teamMember.name && (
                <p className="invite-modal-members-list-item-email">
                  {teamMember.email}
                </p>
              )}
            </div>
          </div>
        </List.Item>
      );
    });

    return (
      <Modal
        centered
        open
        size="tiny"
        closeIcon
        onClose={() => this.setState({ showTeamModal: false })}
      >
        <div className="invite-modal-root">
          <div className="invite-modal-members-top-container">
            <p className="invite-modal-members-title">
              {Strings.INVITE.inviteTeamMembers}
            </p>
          </div>
          <List className="invite-modal-members-list">{TeamMembers}</List>
          <div className="invite-modal-members-divider" />
          <div className="invite-modal-members-bottom-container">
            <Checkbox
              label={Strings.INVITE.selectAll}
              onChange={this.onSelectAllCheckboxChange}
              checked={teamMembers.length === teamModalEmails.length}
            />
            <div style={{ flexGrow: 1 }} />
            <Button
              className="common-green-button"
              disabled={teamModalEmails.length === 0}
              onClick={this.onClickAdd}
            >
              {Strings.INVITE.add}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  render() {
    const { showTeamModal } = this.state;
    return (
      <div>
        {this.renderInviteModal()}
        {showTeamModal && this.renderTeamModal()}
      </div>
    );
  }
}

InviteDialog.propTypes = {
  dispatch: PropTypes.func.isRequired,
  isInviting: PropTypes.bool.isRequired,
  teamMembers: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }),
  ).isRequired,
  subscription: PropTypes.shape({
    status: PropTypes.string.isRequired,
  }).isRequired,
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      teamMembers: PropTypes.arrayOf(
        PropTypes.shape({
          email: PropTypes.string,
          status: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
};

const mapStateToProps = state => ({
  isInviting: state.uiState.isInviting,
  teamMembers: state.teamMembers,
  subscription: state.premiumSubscription.subscription,
  projects: state.projects,
});

export default connect(mapStateToProps)(InviteDialog);



// WEBPACK FOOTER //
// ./src/components/dialog/InviteDialog.js